import { isEmpty, isObject } from 'radash';
import { useMemo } from 'react';

import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { getGroupTitleFilter } from '@/services/angularjs-bridge';
import { logger } from '@/services/logger';
import { DataModelAttribute, DataModelAttributeTypeName, Item } from '@/types/DataModel';

import { useOptionTranslator } from '../hooks/useOptionTranslator';

export const useGroupTitle = ({
    attribute,
    index,
    memberAttributes,
    currentValue,
}: {
  attribute: DataModelAttribute;
  index: number;
  memberAttributes: any; //FIXME: Type
  currentValue: any;
}) => {

    const { translateOption } = useOptionTranslator();
    const { organization } = useOrganization();
    const { user } = useUser();
    const { dataModel } = useDataModel();

    const getTranslatedAttributeValue = ({
        attributeValue,
        attribute,
    }: {
    attributeValue: string;
    attribute: DataModelAttribute;
  }) => {
        const TRANSLATABLE_ATTRIBUTES = [
            DataModelAttributeTypeName.Enum,
            DataModelAttributeTypeName.EnumSet,
            DataModelAttributeTypeName.OpenEnum,
            DataModelAttributeTypeName.OpenEnumSet,
            DataModelAttributeTypeName.Dimensional,
            DataModelAttributeTypeName.Physical,
        ];
        const attributeStr = isObject(attributeValue) ? '' : attributeValue?.toString();
        if (
            attribute?.typeName &&
      TRANSLATABLE_ATTRIBUTES.includes(attribute.typeName)
        ) {
            return translateOption(attributeStr, attribute);
        }
        if (attribute?.typeName === 'Date') {
            return new Date(attributeValue).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
            });
        }
        return attributeStr;
    };

    return useMemo(() => {
        if (!dataModel || !user || !organization) {
            return '';
        }

        const entry = (!isEmpty(currentValue) && currentValue[index] ) ? currentValue[index] : '';

        const defaultGroupTitle = (filterParameters: string[]) => {
            const firstGroupAttribute = memberAttributes[0]?.name;
            if (!isEmpty(filterParameters)) {
                return filterParameters.reduce(
                    (accumulatedResult: string, parameter: string | number) => {
                        const attributeValue = entry?.[parameter];
                        if (isEmpty(attributeValue)) {
                            return accumulatedResult;
                        }
                        const translatedValue = getTranslatedAttributeValue({
                            attributeValue,
                            attribute: memberAttributes?.find(
                                (obj: any) => obj.name === parameter,
                            ),
                        });
                        return `${accumulatedResult}${accumulatedResult ? ' - ' : ''}${translatedValue}`;
                    },
                    '',
                );
            }
            return isEmpty(firstGroupAttribute)
                ? ''
                : getTranslatedAttributeValue({
                    attributeValue: entry?.[firstGroupAttribute] || '',
                    attribute: memberAttributes?.find(
                        (obj: any) => obj.name === firstGroupAttribute,
                    ),
                });
        };

        let filterName = 'defaultGroupTitleFilter';
        let filterParameters: string[] = [];
        const { params } = attribute;

        if (params?.uiGroupTitleFilter) {
            const { uiGroupTitleFilter } = params;
            if (Array.isArray(uiGroupTitleFilter)) {
                const uiGroupTitleFilterObj = uiGroupTitleFilter.reduce(
                    (acc: any, { key, value }: any) => {
                        acc[key] = value;
                        return acc;
                    },
                    {},
                );
                filterName = uiGroupTitleFilterObj.filter_name;
                filterParameters = uiGroupTitleFilterObj.filter_parameters;
            } else if (uiGroupTitleFilter) {
                filterName = uiGroupTitleFilter;
            } else {
                logger.warn('GroupTitleComponent: Could not parse group title filter', attribute);
            }
        }
        if (!filterName || filterName === 'defaultGroupTitleFilter') {
            return defaultGroupTitle(filterParameters);
        }

        const customGroupTitleFilterFn = getGroupTitleFilter(filterName);

        if (!customGroupTitleFilterFn) {
            logger.error(`GroupTitleComponent: Group title filter '${filterName}' defined for attribute '${attribute.name}', could not be found`);
            return '';
        }

        return customGroupTitleFilterFn(
            entry,
            index,
            filterParameters,
            attribute,
            currentValue as Item,
            user,
            organization,
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attribute, currentValue, dataModel, index, organization, user, memberAttributes, translateOption]);
};
